import { TableViewSettingsData, TableVisibilitySettings } from '../types/gotypes'

export const getDefaultTableViewSettings = (): TableViewSettingsData => {
  return {
    visibility: {
      frequency: false,
      column_percent: true,
      row_percent: false,
      significance: false,
    },
    vertical_coefficient: 1.0,
    horizontal_coefficient: 1.0,
    chart_type: 'line',
    data_type: 'freq',
    selected_rows: [],
    selected_columns: [],
    selected_column: '',
    switch_axis: false,
    view_mode: 'table',
  }
}

// Helper to get all possible table setting param names
export const getAllTableSettingParamNames = (prefix = ''): string[] => {
  const paramName = (name: string) => prefix ? `${prefix}_${name}` : name
  return [
    paramName('vertical_coefficient'),
    paramName('horizontal_coefficient'),
    paramName('chart_type'),
    paramName('data_type'),
    paramName('switch_axis'),
    paramName('selected_column'),
    paramName('selected_rows'),
    paramName('selected_columns'),
    paramName('view_mode'),
  ]
}

// Helper to get all possible visibility param names
export const getAllVisibilityParamNames = (prefix = ''): string[] => {
  const paramName = (name: string) => prefix ? `${prefix}_${name}` : name
  return [
    paramName('frequency'),
    paramName('column_percent'),
    paramName('row_percent'),
    paramName('significance'),
  ]
}

export const convertVisibilitySettingsToURL = (
  settings: Partial<TableVisibilitySettings>,
  serverVisibility?: TableVisibilitySettings,
  prefix = ''
): Record<string, string> => {
  const ret: Record<string, string> = {}
  const paramName = (name: string) => prefix ? `${prefix}_${name}` : name

  // Helper to check if value differs from server settings
  const shouldInclude = <K extends keyof TableVisibilitySettings>(
    key: K,
    value: TableVisibilitySettings[K] | undefined
  ): boolean => {
    if (value === undefined) return false
    // If no server settings provided, include all defined values (backward compatibility)
    if (!serverVisibility) return true
    // Simple comparison for boolean values
    return value !== serverVisibility[key]
  }

  if(shouldInclude('frequency', settings.frequency)) {
    ret[paramName('frequency')] = settings.frequency!.toString()
  }
  if(shouldInclude('column_percent', settings.column_percent)) {
    ret[paramName('column_percent')] = settings.column_percent!.toString()
  }
  if(shouldInclude('row_percent', settings.row_percent)) {
    ret[paramName('row_percent')] = settings.row_percent!.toString()
  }
  if(shouldInclude('significance', settings.significance)) {
    ret[paramName('significance')] = settings.significance!.toString()
  }

  return ret
}

export const convertViewSettingsToURL = (
  settings: Partial<TableViewSettingsData>,
  serverSettings?: TableViewSettingsData,
  prefix = ''
): Record<string, string> => {
  const ret: Record<string, string> = {}
  const paramName = (name: string) => prefix ? `${prefix}_${name}` : name

  // Helper to check if value differs from server settings
  const shouldInclude = <K extends keyof TableViewSettingsData>(
    key: K,
    value: TableViewSettingsData[K] | undefined
  ): boolean => {
    if (value === undefined) return false
    // If no server settings provided, include all defined values (backward compatibility)
    if (!serverSettings) return true
    // For arrays, do a deep comparison
    if (Array.isArray(value) && Array.isArray(serverSettings[key])) {
      return JSON.stringify(value) !== JSON.stringify(serverSettings[key])
    }
    // For other values, simple comparison
    return value !== serverSettings[key]
  }

  if(shouldInclude('vertical_coefficient', settings.vertical_coefficient)) {
    ret[paramName('vertical_coefficient')] = settings.vertical_coefficient!.toString()
  }
  if(shouldInclude('horizontal_coefficient', settings.horizontal_coefficient)) {
    ret[paramName('horizontal_coefficient')] = settings.horizontal_coefficient!.toString()
  }
  if(shouldInclude('chart_type', settings.chart_type)) {
    ret[paramName('chart_type')] = settings.chart_type!
  }
  if(shouldInclude('data_type', settings.data_type)) {
    ret[paramName('data_type')] = settings.data_type!
  }
  if(shouldInclude('switch_axis', settings.switch_axis)) {
    ret[paramName('switch_axis')] = settings.switch_axis!.toString()
  }
  if(shouldInclude('selected_column', settings.selected_column)) {
    ret[paramName('selected_column')] = settings.selected_column!
  }
  if(shouldInclude('selected_rows', settings.selected_rows)) {
    ret[paramName('selected_rows')] = settings.selected_rows!.join(',')
  }
  if(shouldInclude('selected_columns', settings.selected_columns)) {
    ret[paramName('selected_columns')] = settings.selected_columns!.join(',')
  }
  if(shouldInclude('view_mode', settings.view_mode)) {
    ret[paramName('view_mode')] = settings.view_mode!
  }

  return ret
}

export const getURLBasedVisibilitySettings = (prefix = ''): Partial<TableVisibilitySettings> => {
  const params = new URLSearchParams(window.location.search)
  const param = (name: string) => {
    const fullName = prefix ? `${prefix}_${name}` : name
    return params.get(fullName) || ''
  }

  const ret: Partial<TableVisibilitySettings> = {}

  if(param('frequency') === 'true') {
    ret.frequency = true
  } else if(param('frequency') === 'false') {
    ret.frequency = false
  }

  if(param('column_percent') === 'true') {
    ret.column_percent = true
  } else if(param('column_percent') === 'false') {
    ret.column_percent = false
  }

  if(param('row_percent') === 'true') {
    ret.row_percent = true
  } else if(param('row_percent') === 'false') {
    ret.row_percent = false
  }

  if(param('significance') === 'true') {
    ret.significance = true
  } else if(param('significance') === 'false') {
    ret.significance = false
  }

  return ret
}

export const getURLBasedSettings = (prefix = ''): Partial<TableViewSettingsData> => {
  const params = new URLSearchParams(window.location.search)
  const param = (name: string) => {
    const fullName = prefix ? `${prefix}_${name}` : name
    return params.get(fullName) || ''
  }

  const ret: Partial<TableViewSettingsData> = {}

  if(param('vertical_coefficient')) {
    ret.vertical_coefficient = parseFloat(param('vertical_coefficient'))
  }

  if(param('horizontal_coefficient')) {
    ret.horizontal_coefficient = parseFloat(param('horizontal_coefficient'))
  }

  if(param('chart_type')) {
    ret.chart_type = param('chart_type')
  }

  if(param('data_type')) {
    ret.data_type = param('data_type')
  }
  
  if(param('switch_axis') === 'true') {
    ret.switch_axis = true
  } else if(param('switch_axis') === 'false') {
    ret.switch_axis = false
  }

  if(param('selected_rows')) {
    ret.selected_rows = param('selected_rows').split(',')
  }

  if(param('selected_columns')) {
    ret.selected_columns = param('selected_columns').split(',')
  }

  if(param('selected_column')) {
    ret.selected_column = param('selected_column')
  }

  if(param('view_mode')) {
    ret.view_mode = param('view_mode')
  }

  return ret
}

export const getMergedURLTableViewSettings = (serverSettings: TableViewSettingsData, prefix = ''): TableViewSettingsData => {
  const baseURLSettings = getURLBasedSettings(prefix)
  const visibilityURLSettings = getURLBasedVisibilitySettings(prefix)

  return {
    ...serverSettings,
    ...baseURLSettings,
    visibility: {
      ...serverSettings.visibility,
      ...visibilityURLSettings,
    }
  }
}

/**
 * The tables-module chart view's Data Type default (#1173 fault 4).
 *
 * TABLE and CHART share one `data_type` field on TableViewSettingsData; the
 * table half never reads it (Frequencies/Column %/Row % render together via
 * `visibility`, not `data_type`), so the shared baked-in default of 'freq'
 * (getDefaultTableViewSettings) is free for the chart to default differently
 * without disturbing table view. A user's own pick (URL override) or a
 * previously saved view setting still wins — only a genuinely untouched
 * table opens its chart on Column %, matching the dashboard chart widget's
 * existing default. Applied at the chart-view seam (PlatinumTableWrapper)
 * rather than by mutating the shared stored default.
 */
export function resolveChartDataType(params: { currentDataType: string; explicit: boolean }): string {
  return params.explicit ? params.currentDataType : 'colPct'
}

/** Check if a table path belongs to a specific user */
export function isUserTable(path: string, email: string): boolean {
  if (!path || !email) return false
  return path.toLowerCase().startsWith(`tables/user/${email.toLowerCase()}/`)
}

/** Check if a table path is an Exec table */
export function isExecTable(path: string): boolean {
  return path.toLowerCase().startsWith('tables/exec/')
}

/** Extract the Exec subfolder from a path like Tables/Exec/Charts/Report.cbt */
export function extractExecFolder(path: string): string {
  if (!isExecTable(path)) return ''
  const parts = path.replace(/\\/g, '/').split('/')
  if (parts.length <= 3) return ''
  return parts.slice(2, -1).join('/')
}

/** Extract the folder under a user's My Tables from a path like Tables/User/a@b.com/Q3/Report.cbt */
export function extractUserFolder(path: string, email: string): string {
  if (!isUserTable(path, email)) return ''
  const parts = path.replace(/\\/g, '/').split('/')
  if (parts.length <= 4) return ''
  return parts.slice(3, -1).join('/')
}
